import React from "react";
import Ets2AffidavitSimplifiedDTO from "features/ets2/dtos/Ets2AffidavitSimplifiedDTO";
import { useTranslation } from "react-i18next";
import RoundButton from "features/shared/components/buttons/RoundButton";
import Ets2AffidavitStatusLabel from "features/ets2/components/Ets2AffidavitStatusLabel";
import InfoItemMobile from "features/shared/components/infoItem/InfoItemMobile";
import { formatDate } from "features/shared/helpers/Formatters";
import { Ets2AffidavitStatus } from "../enums/ets2AffidavitStatus";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import IContentDialogService from "features/shared/components/contentDialog/IContentDialogService";
import ContentDialogConfirm from "features/shared/components/contentDialog/ContentDialogConfirm";

interface Props {
  affidavit: Ets2AffidavitSimplifiedDTO;
  onCancell?: (affidavitId: number | string) => void;
}

const NON_CANCELLABLE_STATUSES: Ets2AffidavitStatus[] = [
  "ProcessedApproved",
  "ProcessedRejected",
  "ProcessedMixed",
  "ExceptionCreateError",
  "Cancelled",
];

const Ets2AffidavitSimplified = ({ affidavit, onCancell }: Props) => {
  const tLabels = useTranslation("ets2\\ets2").t;

  const dialogService = useInjection<IContentDialogService>(IocTypes.ContentDialogService);

  const showCancellButton =
    !!onCancell &&
    !!affidavit.ets2AffidavitStatus &&
    !NON_CANCELLABLE_STATUSES.includes(affidavit.ets2AffidavitStatus);

  const handleCancellClick = () => {
    if (!onCancell) return;

    dialogService.openDialog(
      tLabels("ets2AffidavitSimplifiedHeader.cancell"),
      () => (
        <ContentDialogConfirm
          message={tLabels(
            "ets2AffidavitSimplifiedHeader.cancellMessage"
          )}
          action={() => onCancell(affidavit.ets2AffidavitId!)}
        />
      )
    );
  };

  const actionsTitle = showCancellButton
    ? `${tLabels("ets2AffidavitSimplifiedHeader.cancell")} / ${tLabels(
        "ets2AffidavitSimplifiedHeader.detail"
      )}`
    : tLabels("ets2AffidavitSimplifiedHeader.detail");

  return (
    <div className="row no-gutters align-items-center">
      <div className="col-sm">
        <div className="row no-gutters align-items-center">
          <div className="col-sm-3 pr-sm-2">
            <InfoItemMobile
              title={tLabels("ets2AffidavitSimplifiedHeader.affidavitNumber")}
              value={
                <div
                  className="text-truncate"
                  title={affidavit.affidavitNumber}
                >
                  {affidavit.affidavitNumber}
                </div>
              }
              hideTitleOn="sm"
            />
          </div>
          <div className="col-sm-2 pr-sm-2 text-sm-center">
            <InfoItemMobile
              title={tLabels("ets2AffidavitSimplifiedHeader.year")}
              value={affidavit.year != null ? String(affidavit.year) : ""}
              hideTitleOn="sm"
            />
          </div>
          <div className="col-sm-4 pr-sm-2 text-sm-center">
            <InfoItemMobile
              title={tLabels("ets2AffidavitSimplifiedHeader.createdDate")}
              value={formatDate(affidavit.createdDate!) ?? ""}
              hideTitleOn="sm"
            />
          </div>
          <div className="col-sm-3">
            <InfoItemMobile
              title={tLabels("ets2AffidavitSimplifiedHeader.status")}
              value={
                <Ets2AffidavitStatusLabel
                  status={affidavit.ets2AffidavitStatus!}
                  className="text-truncate mw-100"
                />
              }
              hideTitleOn="sm"
            />
          </div>
        </div>
      </div>
      <div className="col-sm-auto pl-sm-3">
        <InfoItemMobile
          title={actionsTitle}
          value={
            <div className="d-flex">
              <RoundButton
                icon="ban"
                size="sm"
                onClickHandler={showCancellButton ? handleCancellClick : undefined}
                className={`mr-2${showCancellButton ? "" : " invisible pointer-events-none"}`}
              />
              <RoundButton
                asLink
                to={`/ets2-affidavit/${affidavit.ets2AffidavitId}`}
              />
            </div>
          }
          hideTitleOn="sm"
        />
      </div>
    </div>
  );
};

export default Ets2AffidavitSimplified;
